import React from "react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { BUSINESS_INFO } from "../config";
import { GallerySection } from "../components/GallerySection";
import { getOptimizedImageUrl } from "../lib/utils";

export function PuppyPage() {
  const steps = [
    {
      number: "1",
      title: "1. besøg: Øv på klippebordet",
      badge: "Besøg 1",
      description:
        "Hvalpen introduceres i ro og mag til salonens dufte, lyde og klippebordet. Vi hygger, giver godbidder og øver let håndtering af poter, ører og pels, så bordet bliver et trygt og rart sted.",
      icon: "fa-paw",
    },
    {
      number: "2",
      title: "2. besøg: Vask og føn",
      badge: "Besøg 2",
      description:
        "Når tilliden er etableret, øver vi den behagelige lunkne vask samt den blide føntørrer med lav hastighed og behagelig temperatur. Vi bruger en mild, økologisk hvalpeshampoo særligt tilpasset hvalpens sarte hud og pels.",
      icon: "fa-shower",
    },
    {
      number: "3",
      title: "3. besøg: Vask, føn, klip og negle",
      badge: "Besøg 3",
      description:
        "Den fulde behandling, hvor hvalpen nu er helt tryg ved rammerne. Vi vasker, føntørrer, klipper pelsen nænsomt og tjekker og klipper kløerne med masser af ros.",
      icon: "fa-scissors",
    },
  ];

  return (
    <>
      <SEO
        title="Hvalpepakke - Hundens Pels og Pleje"
        description="Giv din hvalp en god og tryg start hos hundefrisøren i Fredericia med vores 3-trins hvalpepakke. Tryghed, tålmodighed og kærlig tilvænning."
      />

      {/* Hero Header - No box behind heading and description */}
      <section className="relative overflow-hidden border-b border-[#E8DFD5] bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            {/* Left text */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-black leading-tight">
                Hvalpepakke <br />
                <span className="font-normal text-xl sm:text-2xl lg:text-3xl text-neutral-700">– Den trygge start hos hundefrisøren</span>
              </h1>

              <p className="text-lg text-neutral-800 leading-relaxed">
                Formålet med vores hvalpepakke er at give hvalpen en god og tryg introduktion til hundefrisøren. Gennem tre rolige besøg opbygger vi tillid, glæde og tryghed ved lyde, udstyr og berøring – altid med milde, økologiske plejeprodukter og hvalpeshampoo tilpasset den lille hvalps behov.
              </p>

              {/* Pricing Callout */}
              <div className="py-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl sm:text-4xl font-bold font-heading text-black">
                    1.600,00 kr.
                  </span>
                  <span className="text-sm text-neutral-600">
                    (Inkluderer alle 3 besøg)
                  </span>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <a
                  href={BUSINESS_INFO.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 rounded-full bg-[#dabeb9] px-8 py-3.5 text-sm sm:text-base font-medium tracking-wide text-black shadow-xs transition-colors hover:bg-[#c9a7a0] active:scale-98"
                >
                  <i className="fa-solid fa-calendar-check text-black" aria-hidden="true" />
                  <span>Book hvalpepakke online</span>
                </a>

                {/* Secondary CTA: NO background color and NO hover background color */}
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-full border border-black bg-transparent px-7 py-3.5 text-sm sm:text-base font-medium tracking-wide text-black transition-colors hover:bg-transparent hover:text-black hover:border-black"
                >
                  <i className="fa-solid fa-comment-dots text-black" aria-hidden="true" />
                  <span>Tag gerne kontakt for en snak</span>
                </Link>
              </div>
            </div>

            {/* Right Image - STRICTLY NO TEXT ON IMAGE */}
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-3xl shadow-sm ring-1 ring-[#E2D2C3]">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003534.jpg",
                    { width: 800, quality: 85 }
                  )}
                  alt="Sød lille hvalp svøbt i et blødt håndklæde efter tryg vask og pleje i salonen hos Hundens Pels og Pleje"
                  className="h-[420px] w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DE TRE BESØG (3-TRINS FORLØB) */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#FEFAF3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-black">
              Sådan foregår hvalpepakken
            </h2>

            <p className="mt-3 text-base text-neutral-700 leading-relaxed">
              En god start i hvalpestadiet lægger grundstenen for et helt hundeliv med glæde og tryghed hos frisøren.
            </p>

            <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-neutral-800">
              <i className="fa-regular fa-clock text-black" aria-hidden="true" />
              <span>Varighed: 1 time pr. session</span>
            </div>
          </div>

          {/* Cards: NO boxes behind icons/numbers */}
          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="relative flex flex-col justify-between rounded-3xl border border-[#E8DFD5]/80 bg-white p-8 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-shadow duration-300"
              >
                <div>
                  <h3 className="font-heading text-lg font-medium text-black">
                    {step.title}
                  </h3>

                  <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                <div className="mt-8 pt-4 border-t border-[#E8DDD2]">
                  <span className="text-xs font-medium text-neutral-800">
                    Masser af godbidder & ro
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Special quote callout */}
          <div className="mt-16 text-center max-w-2xl mx-auto">
            <i className="fa-solid fa-quote-left text-2xl text-[#dabeb9]" aria-hidden="true" />
            <p className="mt-3 font-heading text-lg sm:text-xl text-black leading-relaxed font-medium">
              "En samlet pakke så hvalpen får en god start hos hundefrisøren. Tag gerne kontakt, så tager vi en snak."
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              <a
                href={BUSINESS_INFO.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#dabeb9] px-7 py-3.5 text-sm font-bold text-black shadow-xs hover:bg-[#c9a7a0]"
              >
                <i className="fa-solid fa-calendar-check text-black" aria-hidden="true" />
                <span>Book hvalpepakken her</span>
              </a>

              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full border border-black bg-transparent px-6 py-3.5 text-sm font-semibold text-black transition-colors hover:bg-black hover:text-white whitespace-nowrap"
              >
                <i className="fa-solid fa-comment-dots text-current" aria-hidden="true" />
                <span>Kontakt os for en snak</span>
              </Link>

              <a
                href={`tel:${BUSINESS_INFO.phoneRaw}`}
                className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-transparent px-5 py-3.5 text-sm font-medium text-neutral-800 transition-colors hover:border-black hover:text-black whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-black" aria-hidden="true" />
                <span className="whitespace-nowrap">{BUSINESS_INFO.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* DEDIKERET HVALPEGALLERI */}
      <GallerySection
        categorySlug="hvalpe"
        title="Hvalpe i salonen"
        subtitle="Glade, nysgerrige og trygge hvalpe under deres første besøg på klippebordet."
        showTabs={false}
      />
    </>
  );
}
